"use client";

/**
 * SignaturesToggleButton — chevron + count, toggles voter dropdown panel
 *
 * Figma 5462:53124:
 *   V1: transparent bg, border border-stroke-light (neutral stroke variant)
 *   V2/V3: border border-accent (accent stroke variant)
 *
 * Layout: inline-flex gap-2 items-center, px-3 py-1.5, rounded-lg, text-sm
 *
 * Forwards ref + extra button props so Radix Popover.Trigger asChild can
 * inject its own onClick/aria-* without us redefining them here.
 */

import { ArrowDown2 } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

interface SignaturesToggleButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  count: number;
  isOpen: boolean;
  accentVariant: boolean;
}

export const SignaturesToggleButton = forwardRef<
  HTMLButtonElement,
  SignaturesToggleButtonProps
>(({ count, isOpen, accentVariant, className, ...props }, ref) => {
  const t = useTranslations("dao");
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
        accentVariant
          ? "border border-accent text-accent bg-transparent"
          : "border border-stroke-light text-text-primary bg-transparent hover:bg-bg-secondary",
        className,
      )}
    >
      <span>
        <span dir="ltr">{count}</span> {t("proposals.shared.signature")}
      </span>
      {/* Chevron only renders when there's something to reveal. At count=0
          MetaRow skips the Popover wrapper, so the affordance would be a lie. */}
      {count > 0 && (
        <ArrowDown2
          size={16}
          className={cn(
            "transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      )}
    </button>
  );
});
SignaturesToggleButton.displayName = "SignaturesToggleButton";
